import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { getSpellDefinition } from '@shared/data/spells';

/**
 * Casteo de hechizos con targeting: envía el cast al server (sendSpellCast, con validaciones de
 * mana/GCD/rango), entra/sale del modo de apuntado de hechizos de área (enterSpellTargeting/
 * exitSpellTargeting) y dibuja el preview de tiles afectados siguiendo el puntero
 * (updateSpellTargetingPreview). Extraído de GameScene (god-object). El estado (spellTargeting,
 * cooldowns…) vive en GameScene y se accede vía `this.scene.*`. Ver docs/SPELLS.md, docs/TARGETING.md.
 */
export class SpellTargetingController {
  constructor(private scene: GameScene) {}

  public sendSpellCast(spellId: string): void {
    if (!this.scene.colyseusClient || !this.scene.localSessionId || !this.scene.room) return;
    const spell = getSpellDefinition(spellId);
    if (!spell) return;

    // Apretar un hotkey de hechizo rompe la protección de aparición, AUNQUE el
    // casteo no se concrete (sin target / cooldown). Avisar al server por si no
    // se manda ningún cast_spell. Ver docs/SPAWN_PROTECTION.md.
    this.scene.stateVfx.breakLocalSpawnProtection();

    // Check if spell is on cooldown (don't enter targeting, don't send to server)
    if (this.scene.spellQuickBar?.isOnCooldown(spellId)) {
      this.scene.overheadText.showSelfErrorText('Hechizo en enfriamiento');
      return;
    }

    // Global spell interval check
    const now = Date.now();
    if (now - this.scene.lastSpellCastTime < this.scene.SPELL_INTERVAL_MS) return;

    // Resource validation is done server-side (avoids desync issues)
    // If server rejects, onError handler shows the red text

    // Cooldown starts only when server confirms success (via onSpellCastResult/onAuraToggled)

    if (spell.castType === 'toggle') {
      this.scene.lastSpellCastTime = now;
      this.scene.colyseusClient.send({ type: 'toggle_spell' as any, spellId } as any);
      return;
    }

    if (spell.castType === 'self') {
      this.scene.lastSpellCastTime = now;
      this.scene.colyseusClient.send({ type: 'cast_spell' as any, spellId } as any);
      return;
    }

    if (spell.castType === 'screen') {
      this.scene.lastSpellCastTime = now;
      this.scene.colyseusClient.send({ type: 'cast_spell' as any, spellId } as any);
      return;
    }

    // === TARGET LOCK FLOW (paradigma WoW) ===
    // Target spells: usar selectedTargetId. Sin target => error.
    if (spell.castType === 'target') {
      if (!this.scene.targets.selectedTargetId) {
        this.scene.overheadText.showSelfErrorText('Sin objetivo');
        return;
      }
      this.scene.lastSpellCastTime = now;
      this.scene.colyseusClient.send({
        type: 'cast_spell' as any,
        spellId,
        targetId: this.scene.targets.selectedTargetId,
      } as any);
      return;
    }

    // Area spells: auto-centrar en el tile del target. Sin target => error
    // (decisión del usuario: no fallback a crosshair).
    if (spell.castType === 'area') {
      const pos = this.scene.targets.getTargetWorldTile();
      if (!pos) {
        this.scene.overheadText.showSelfErrorText('Sin objetivo');
        return;
      }
      this.scene.lastSpellCastTime = now;
      this.scene.colyseusClient.send({
        type: 'cast_spell' as any,
        spellId,
        targetX: pos.x,
        targetY: pos.y,
      } as any);
      return;
    }

    // Tipos restantes (ground puro, futuro): por compatibilidad entran al modo crosshair clásico.
    // Reservado para futuros ground-spells; no se llega acá con los tipos actuales.
    this.enterSpellTargeting(spellId, spell.areaSize ?? 1);
  }

  public enterSpellTargeting(spellId: string, areaSize: number): void {
    // Cancel previous targeting
    this.exitSpellTargeting();

    const preview = this.scene.add.graphics().setDepth(500).setScrollFactor(1);
    this.scene.spellTargeting = { spellId, areaSize, preview, startedAt: Date.now() };

    // Change cursor to crosshair (no tenemos .cur de mira; keyword nativo)
    this.scene.cursor.applyRawCursor('crosshair');
  }

  public exitSpellTargeting(): void {
    if (!this.scene.spellTargeting) return;
    this.scene.spellTargeting.preview?.destroy();
    this.scene.spellTargeting = null;
    this.scene.cursor.setGameCursor('main');
  }

  public updateSpellTargetingPreview(): void {
    if (!this.scene.spellTargeting?.preview) return;

    const pointer = this.scene.input.activePointer;
    const cam = this.scene.cameras.main;
    const worldX = pointer.x + cam.scrollX;
    const worldY = pointer.y + cam.scrollY;

    // Convert to tile
    const TILE = 32; // TILE_SIZE
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);
    const radius = Math.floor(this.scene.spellTargeting.areaSize / 2);

    const gfx = this.scene.spellTargeting.preview;
    gfx.clear();

    // Draw area preview (rings around center tile)
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        const tx = (tileX + dx) * TILE;
        const ty = (tileY + dy) * TILE;

        if (dx === 0 && dy === 0) {
          // Center tile — brighter
          gfx.fillStyle(0xff8800, 0.3);
          gfx.fillRect(tx, ty, TILE, TILE);
          gfx.lineStyle(1, 0xff8800, 0.7);
          gfx.strokeRect(tx, ty, TILE, TILE);
        } else {
          // Ring tiles
          gfx.fillStyle(0xffaa00, 0.15);
          gfx.fillRect(tx, ty, TILE, TILE);
          gfx.lineStyle(1, 0xffaa00, 0.4);
          gfx.strokeRect(tx, ty, TILE, TILE);
        }
      }
    }
  }

}
